(() => {
  if (customElements.get('ff-whatsapp-store')) return;

  class FfWhatsappStore extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const source = this.querySelector('script[type="application/json"][data-wa-config]');
      if (!source) return;
      let config;
      try { config = JSON.parse(source.textContent); } catch { return; }
      if (!config || typeof config !== 'object' || !Array.isArray(config.products)) return;
      this.ready = true;
      this.config = config;
      this.strings = config.strings && typeof config.strings === 'object' ? config.strings : {};
      this.currency = typeof config.currency === 'string' && /^[A-Z]{3}$/.test(config.currency) ? config.currency : 'INR';
      this.country = typeof config.country === 'string' ? config.country.toUpperCase() : '';
      try {
        this.formatter = new Intl.NumberFormat(config.locale || 'en-IN', { style: 'currency', currency: this.currency });
      } catch {
        this.formatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: this.currency });
      }
      this.phone = typeof config.phone === 'string' && /^\d{11,15}$/.test(config.phone) ? config.phone : '';
      this.trialActive = config.trialRules?.enabled === true && config.currency === 'INR' && this.country === 'IN';
      this.variants = new Map();
      this.products = this.normalizeProducts(config.products);
      this.basket = new Map();
      this.rows = new Map();
      this.selectedVariants = new Map();
      this.filter = 'all';
      this.query = '';
      this.limit = 12;
      this.storageKey = `ffwa:v1:${this.currency}:${this.country}`;
      this.restore();
      this.bind();
      this.renderGrid();
      this.renderBasket();
      this.querySelectorAll('[data-wa-chat]').forEach(link => this.setWhatsappLink(link, this.t('chat_message'), true));
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      clearTimeout(this.searchTimer);
      clearTimeout(this.announcementTimer);
      clearTimeout(this.announcementHideTimer);
    }

    t(key, values = {}) {
      const value = this.strings[key];
      if (typeof value !== 'string') return '';
      return value.replace(/\{(\w+)\}/g, (match, name) => values[name] === undefined ? match : String(values[name]));
    }

    money(value) {
      return this.formatter.format((Number.isSafeInteger(value) && value >= 0 ? value : 0) / 100);
    }

    node(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    safeUrl(value, product = false) {
      if (typeof value !== 'string' || !value.trim()) return '';
      try {
        const url = new URL(value, window.location.origin);
        if (url.protocol !== 'https:' && !(url.protocol === 'http:' && url.origin === window.location.origin)) return '';
        if (product && url.origin !== window.location.origin) return '';
        return url.href;
      } catch { return ''; }
    }

    normalizeProducts(entries) {
      const products = [];
      const ids = new Set();
      for (const entry of entries) {
        if (!entry || typeof entry !== 'object' || typeof entry.id !== 'string' || ids.has(entry.id)) continue;
        if (typeof entry.title !== 'string' || !entry.title.trim() || !Array.isArray(entry.variants)) continue;
        const url = this.safeUrl(entry.url, true);
        if (!url) continue;
        const product = {
          id: entry.id, title: entry.title.slice(0, 240), url,
          image: this.safeUrl(entry.image), category: ['a2', 'ghee', 'trial'].includes(entry.category) ? entry.category : 'a2',
          variants: []
        };
        for (const item of entry.variants) {
          if (!item || typeof item.id !== 'string' || !/^[A-Za-z0-9_-]{1,80}$/.test(item.id) || this.variants.has(item.id)) continue;
          if (!Number.isSafeInteger(item.price) || item.price < 0 || item.price > 10000000000) continue;
          const variant = {
            id: item.id, title: typeof item.title === 'string' ? item.title.slice(0, 160) : '',
            price: item.price, compareAt: Number.isSafeInteger(item.compareAt) && item.compareAt > item.price && item.compareAt <= 10000000000 ? item.compareAt : null,
            available: item.available === true, sku: typeof item.sku === 'string' ? item.sku.slice(0, 100) : '',
            image: this.safeUrl(item.image) || product.image, product
          };
          product.variants.push(variant);
          this.variants.set(variant.id, variant);
        }
        if (product.variants.length) {
          ids.add(product.id);
          products.push(product);
        }
      }
      return products;
    }

    unitPrice(variant) {
      return variant.product.category === 'trial' && this.trialActive ? 38900 : variant.price;
    }

    available(variant) {
      return Boolean(variant?.available && (variant.product.category !== 'trial' || this.trialActive));
    }

    trialCount() {
      let total = 0;
      this.basket.forEach((quantity, id) => { if (this.variants.get(id)?.product.category === 'trial') total += quantity; });
      return total;
    }

    totals() {
      let regular = 0;
      let count = 0;
      this.basket.forEach((quantity, id) => {
        const variant = this.variants.get(id);
        count += quantity;
        if (variant.product.category !== 'trial') regular += variant.price * quantity;
      });
      const trials = this.trialCount();
      const trial = trials === 3 ? 99900 : trials * 38900;
      return { regular, trial, count, trials, discount: trials === 3 ? 16800 : 0, total: regular + trial };
    }

    restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (!saved || saved.version !== 1 || !Array.isArray(saved.lines)) return;
        for (const line of saved.lines.slice(0, 100)) {
          if (!line || typeof line.id !== 'string' || !Number.isSafeInteger(line.quantity) || line.quantity < 1 || line.quantity > 20) continue;
          const variant = this.variants.get(line.id);
          if (!this.available(variant) || this.basket.has(line.id) || this.basket.size >= 12) continue;
          const max = variant.product.category === 'trial' ? 3 - this.trialCount() : 20;
          const quantity = line.quantity <= max ? line.quantity : 0;
          if (quantity > 0) this.basket.set(line.id, quantity);
        }
      } catch {}
    }

    persist() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({ version: 1, lines: [...this.basket].map(([id, quantity]) => ({ id, quantity })) }));
      } catch {}
    }

    announce(message) {
      clearTimeout(this.announcementTimer);
      clearTimeout(this.announcementHideTimer);
      this.querySelectorAll('[data-wa-announcement], [data-wa-cart-announcement]').forEach(node => {
        node.textContent = '';
        node.hidden = true;
      });
      this.announcementTimer = setTimeout(() => {
        const target = this.querySelector(this.dialog?.open ? '[data-wa-cart-announcement]' : '[data-wa-announcement]');
        if (!target || !message) return;
        target.hidden = false;
        target.textContent = message;
        this.announcementHideTimer = setTimeout(() => {
          target.hidden = true;
          target.textContent = '';
        }, 4000);
      }, 30);
    }

    bind() {
      const search = this.querySelector('[data-wa-search]');
      search?.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.query = search.value.trim().toLocaleLowerCase().slice(0, 160);
          this.limit = 12;
          this.renderGrid();
        }, 150);
      });
      search?.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !search.value) return;
        event.preventDefault();
        clearTimeout(this.searchTimer);
        search.value = '';
        this.query = '';
        this.limit = 12;
        this.renderGrid();
      });
      this.querySelectorAll('[data-wa-filter]').forEach(button => button.addEventListener('click', () => {
        this.filter = ['all', 'a2', 'ghee', 'trial'].includes(button.dataset.waFilter) ? button.dataset.waFilter : 'all';
        this.limit = 12;
        this.renderGrid();
      }));
      this.querySelector('[data-wa-more]')?.addEventListener('click', () => {
        const previous = this.limit;
        this.limit += 12;
        this.renderGrid();
        const card = this.querySelector('[data-wa-grid]')?.children[previous];
        if (card) {
          card.tabIndex = -1;
          card.focus();
        }
      });
      this.querySelectorAll('[data-wa-open-cart]').forEach(button => button.addEventListener('click', () => this.openCart(button)));
      this.querySelectorAll('[data-wa-close-cart]').forEach(button => button.addEventListener('click', () => this.closeCart()));
      this.dialog = this.querySelector('[data-wa-cart]');
      this.dialog?.addEventListener('close', () => this.returnFocus());
      this.dialog?.addEventListener('cancel', event => { event.preventDefault(); this.closeCart(); });
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closeCart();
      });
      this.querySelector('[data-wa-clear]')?.addEventListener('click', () => {
        this.basket.clear();
        this.renderBasket();
        this.announce(this.t('cleared'));
        this.querySelector('[data-wa-close-cart]')?.focus();
      });
      this.querySelectorAll('[data-wa-name], [data-wa-notes]').forEach(input => input.addEventListener('input', () => this.refreshOrderLink()));
      this.querySelector('[data-wa-whatsapp]')?.addEventListener('click', event => {
        if (!this.basket.size || !this.phone) {
          event.preventDefault();
          this.announce(this.t(this.phone ? 'empty_request' : 'phone_missing'));
          return;
        }
        this.refreshOrderLink();
      });
      this.querySelector('[data-wa-copy]')?.addEventListener('click', () => this.copySummary());
    }

    renderGrid() {
      const grid = this.querySelector('[data-wa-grid]');
      if (!grid) return;
      const matches = this.products.filter(product => {
        if (this.filter !== 'all' && product.category !== this.filter) return false;
        if (!this.query) return true;
        return `${product.title} ${product.variants.map(variant => `${variant.title} ${variant.sku}`).join(' ')}`.toLocaleLowerCase().includes(this.query);
      });
      grid.replaceChildren(...matches.slice(0, this.limit).map(product => this.card(product)));
      this.querySelectorAll('[data-wa-filter]').forEach(button => {
        if (button.tagName === 'BUTTON') button.setAttribute('aria-pressed', String(button.dataset.waFilter === this.filter));
      });
      this.querySelectorAll('[data-wa-count]').forEach(node => { node.textContent = this.t('product_count', { count: matches.length }); });
      const empty = this.querySelector('[data-wa-empty]');
      if (empty) empty.hidden = matches.length !== 0;
      const more = this.querySelector('[data-wa-more]');
      if (more) more.hidden = matches.length <= this.limit;
    }

    picture(url, title, className) {
      const image = this.node('img', className);
      image.alt = title;
      image.width = 600;
      image.height = 600;
      image.loading = 'lazy';
      image.decoding = 'async';
      if (url) {
        const sized = new URL(url);
        sized.searchParams.set('width', '600');
        image.src = sized.href;
        image.srcset = [240, 400, 600].map(width => {
          const candidate = new URL(url);
          candidate.searchParams.set('width', String(width));
          return `${candidate.href} ${width}w`;
        }).join(', ');
        image.sizes = '(min-width: 1100px) 280px, (min-width: 700px) 33vw, 50vw';
      } else image.hidden = true;
      return image;
    }

    card(product) {
      const card = this.node('article', 'ffwa-card');
      card.setAttribute('role', 'listitem');
      card.dataset.productId = product.id;
      const chosen = this.selectedVariants.get(product.id);
      let variant = product.variants.find(item => item.id === chosen) || product.variants.find(item => item.available) || product.variants[0];
      const media = this.node('a', 'ffwa-card__media');
      media.href = product.url;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');
      media.append(this.picture(variant.image, '', 'ffwa-card__image'));
      const body = this.node('div', 'ffwa-card__body');
      const eyebrow = this.node('p', 'ffwa-card__eyebrow', this.t(`category_${product.category}`));
      const heading = this.node('h3', 'ffwa-card__title');
      const title = this.node('a', '', product.title);
      title.href = product.url;
      heading.append(title);
      const prices = this.node('div', 'ffwa-card__prices');
      const price = this.node('span', 'ffwa-card__price');
      const compare = this.node('s', 'ffwa-card__compare');
      prices.append(price, compare);
      const select = this.node('select', 'ffwa-card__variant');
      select.setAttribute('aria-label', this.t('variant', { product: product.title }));
      product.variants.forEach(item => {
        const option = this.node('option', '', item.title);
        option.value = item.id;
        option.selected = item.id === variant.id;
        select.append(option);
      });
      const add = this.node('button', 'ffwa-card__add');
      add.type = 'button';
      add.dataset.waAdd = '';
      const update = () => {
        price.textContent = this.money(this.unitPrice(variant));
        compare.textContent = variant.compareAt && product.category !== 'trial' ? this.money(variant.compareAt) : '';
        compare.hidden = !compare.textContent;
        add.disabled = !this.available(variant);
        add.textContent = this.t(product.category === 'trial' && !this.trialActive ? 'trial_unavailable' : variant.available ? 'add' : 'sold_out');
        add.dataset.variantId = variant.id;
      };
      select.addEventListener('change', () => {
        variant = product.variants.find(item => item.id === select.value) || product.variants[0];
        this.selectedVariants.set(product.id, variant.id);
        media.replaceChildren(this.picture(variant.image, '', 'ffwa-card__image'));
        update();
      });
      add.addEventListener('click', () => this.add(variant.id));
      const details = this.node('a', 'ffwa-card__details', this.t('details'));
      details.href = product.url;
      details.setAttribute('aria-label', `${this.t('details')} — ${product.title}`);
      update();
      body.append(eyebrow, heading, prices, select, add, details);
      card.append(media, body);
      return card;
    }

    add(id) {
      const variant = this.variants.get(id);
      if (!this.available(variant)) {
        this.announce(this.t(variant?.product.category === 'trial' && !this.trialActive ? 'trial_unavailable' : 'unavailable'));
        return false;
      }
      if (!this.basket.has(id) && this.basket.size >= 12) {
        this.announce(this.t('line_limit'));
        return false;
      }
      if (variant.product.category === 'trial' && this.trialCount() >= 3) {
        this.announce(this.t('trial_limit'));
        return false;
      }
      const quantity = this.basket.get(id) || 0;
      if (quantity >= 20) {
        this.announce(this.t('quantity_limit'));
        return false;
      }
      this.basket.set(id, quantity + 1);
      this.renderBasket();
      this.announce(this.t('added', { product: variant.product.title }));
      return true;
    }

    change(id, difference) {
      if (!this.basket.has(id)) return;
      if (difference > 0) {
        if (this.add(id)) this.announce(this.t('quantity_changed'));
        return;
      }
      const quantity = this.basket.get(id) + difference;
      if (quantity <= 0) this.remove(id);
      else {
        this.basket.set(id, quantity);
        this.renderBasket();
        this.announce(this.t('quantity_changed'));
      }
    }

    remove(id) {
      const row = this.rows.get(id);
      const focused = row?.contains(document.activeElement);
      this.basket.delete(id);
      this.renderBasket();
      if (focused) (this.querySelector('[data-wa-lines] button') || this.querySelector('[data-wa-close-cart]'))?.focus();
      this.announce(this.t('removed'));
    }

    line(variant) {
      const row = this.node('li', 'ffwa-line');
      row.dataset.variantId = variant.id;
      const body = this.node('div', 'ffwa-line__body');
      const heading = this.node('h3', 'ffwa-line__title', variant.product.title);
      const size = this.node('p', 'ffwa-line__variant', variant.title);
      const price = this.node('p', 'ffwa-line__price');
      price.dataset.linePrice = '';
      const controls = this.node('div', 'ffwa-line__controls');
      const minus = this.node('button', 'ffwa-line__minus', '−');
      const plus = this.node('button', 'ffwa-line__plus', '+');
      const quantity = this.node('span', 'ffwa-line__quantity');
      quantity.dataset.lineQuantity = '';
      const remove = this.node('button', 'ffwa-line__remove', this.t('remove', { product: variant.product.title }));
      minus.setAttribute('aria-label', this.t('decrease', { product: `${variant.product.title} ${variant.title}` }));
      plus.setAttribute('aria-label', this.t('increase', { product: `${variant.product.title} ${variant.title}` }));
      minus.type = plus.type = remove.type = 'button';
      minus.addEventListener('click', () => this.change(variant.id, -1));
      plus.addEventListener('click', () => this.change(variant.id, 1));
      remove.addEventListener('click', () => this.remove(variant.id));
      controls.append(minus, quantity, plus, remove);
      body.append(heading, size, price, controls);
      row.append(this.picture(variant.image, '', 'ffwa-line__image'), body);
      return row;
    }

    renderBasket() {
      const total = this.totals();
      const lines = this.querySelector('[data-wa-lines]');
      for (const [id, row] of this.rows) {
        if (!this.basket.has(id)) { row.remove(); this.rows.delete(id); }
      }
      this.basket.forEach((quantity, id) => {
        const variant = this.variants.get(id);
        let row = this.rows.get(id);
        if (!row) {
          row = this.line(variant);
          this.rows.set(id, row);
          lines?.append(row);
        }
        const amount = row.querySelector('[data-line-quantity]');
        amount.textContent = String(quantity);
        amount.setAttribute('aria-label', this.t('quantity', { quantity }));
        row.querySelector('[data-line-price]').textContent = this.money(this.unitPrice(variant) * quantity);
        row.querySelector('.ffwa-line__plus').disabled = quantity >= 20 || (variant.product.category === 'trial' && total.trials >= 3);
      });
      this.querySelectorAll('[data-wa-cart-count]').forEach(node => { node.textContent = String(total.count); });
      this.querySelectorAll('[data-wa-cart-total]').forEach(node => { node.textContent = this.money(total.total); });
      this.querySelectorAll('[data-wa-regular-subtotal]').forEach(node => { node.textContent = this.money(total.regular); });
      this.querySelectorAll('[data-wa-trial-subtotal]').forEach(node => { node.textContent = this.money(total.trial); });
      this.querySelectorAll('[data-wa-cart-empty]').forEach(node => { node.hidden = total.count > 0; });
      this.querySelectorAll('[data-wa-cart-content]').forEach(node => { node.hidden = total.count === 0; });
      this.querySelectorAll('[data-wa-mobile-bar]').forEach(node => { node.hidden = total.count === 0; });
      this.querySelectorAll('[data-wa-trial-note]').forEach(node => {
        node.hidden = total.trials === 0;
        node.textContent = `${this.t('trial_note')}${total.discount ? ` ${this.t('trial_discount', { amount: this.money(total.discount) })}` : ''}`;
      });
      this.querySelectorAll('[data-wa-delivery-note]').forEach(node => { node.textContent = this.t('delivery_note'); });
      const clear = this.querySelector('[data-wa-clear]');
      if (clear) clear.disabled = total.count === 0;
      const copy = this.querySelector('[data-wa-copy]');
      if (copy) copy.disabled = total.count === 0;
      this.refreshOrderLink();
      this.persist();
    }

    message() {
      const totals = this.totals();
      const name = (this.querySelector('[data-wa-name]')?.value || '').trim().slice(0, 60);
      const notes = (this.querySelector('[data-wa-notes]')?.value || '').trim().slice(0, 300);
      const parts = [this.t('order_heading')];
      if (name) parts.push(this.t('order_name', { name }));
      parts.push('');
      this.basket.forEach((quantity, id) => {
        const variant = this.variants.get(id);
        parts.push(this.t('order_item', { product: variant.product.title, variant: variant.title, quantity, amount: this.money(this.unitPrice(variant) * quantity) }));
        if (variant.sku) parts.push(this.t('order_sku', { sku: variant.sku }));
        parts.push(variant.product.url, '');
      });
      parts.push(this.t('order_regular', { amount: this.money(totals.regular) }));
      if (totals.trials) {
        parts.push(this.t('order_trial', { amount: this.money(totals.trial) }));
        if (totals.discount) parts.push(this.t('order_discount', { amount: this.money(totals.discount) }));
        parts.push(this.t('trial_note'));
      }
      parts.push(this.t('order_total', { amount: this.money(totals.total) }), this.t('order_currency', { currency: this.currency }));
      if (notes) parts.push('', this.t('order_notes', { notes }));
      if (!totals.trials && this.basket.size && [...this.basket.keys()].every(id => /^[0-9]+$/.test(id))) {
        const items = [...this.basket].map(([id, quantity]) => `${id}:${quantity}`).join(',');
        const checkout = new URL(`/cart/${items}`, window.location.origin);
        checkout.searchParams.set('discount', 'FIRST10');
        parts.push('', `Secure checkout link: ${checkout.href}`);
        parts.push('Offer: FIRST10 gives 10% off eligible regular products. Final price and delivery are confirmed at checkout.');
      }
      parts.push('', totals.trials ? this.t('order_confirm') : 'Open the secure checkout link above to confirm delivery and pay via PayU. Reply here if you need help.');
      return parts.join('\n');
    }

    setWhatsappLink(link, message, enabled) {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      if (this.phone && enabled) {
        link.href = `https://wa.me/${this.phone}?text=${encodeURIComponent(message)}`;
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      }
    }

    refreshOrderLink() {
      const link = this.querySelector('[data-wa-whatsapp]');
      if (link) this.setWhatsappLink(link, this.message(), this.basket.size > 0);
      if (!this.phone) this.announce(this.t('phone_missing'));
    }

    openCart(opener) {
      if (!this.dialog) return;
      this.opener = opener;
      if (!this.dialog.open) {
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      }
      this.querySelector('[data-wa-close-cart]')?.focus();
    }

    closeCart() {
      if (!this.dialog?.open) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else { this.dialog.removeAttribute('open'); this.returnFocus(); }
    }

    returnFocus() {
      if (this.opener?.isConnected) this.opener.focus();
    }

    async copySummary() {
      if (!this.basket.size) { this.announce(this.t('empty_request')); return; }
      const summary = this.message();
      try {
        if (!navigator.clipboard?.writeText) throw new Error();
        await navigator.clipboard.writeText(summary);
        this.announce(this.t('copied'));
      } catch {
        let field = this.querySelector('[data-wa-copy-fallback]');
        if (!field) {
          field = this.node('textarea', 'ffwa-copy-fallback');
          field.dataset.waCopyFallback = '';
          field.readOnly = true;
          field.rows = 8;
          field.setAttribute('aria-label', this.t('copy_summary'));
          (this.querySelector('[data-wa-cart-content]') || this).append(field);
        }
        field.value = summary;
        field.focus();
        field.select();
        this.announce(this.t('copy_failed'));
      }
    }
  }

  customElements.define('ff-whatsapp-store', FfWhatsappStore);
})();
